import {
    Platform,
    StyleSheet,
    View,
    ColorValue,
    ViewProps,
    Image,
    StyleProp,
    TextStyle,
    TextLayoutLine,
} from "react-native";
import React, {
    PropsWithChildren,
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";
import { useTheme } from "../hooks/use-theme";
import Markdown, {
    RenderFunction,
    MarkdownIt,
    ASTNode,
} from "@code-atlas/react-native-markdown-display";
import hasParents from "@code-atlas/react-native-markdown-display/src/lib/util/hasParents";
import textStyleProps from "@code-atlas/react-native-markdown-display/src/lib/data/textStyleProps";
import openUrl from "@code-atlas/react-native-markdown-display/src/lib/util/openUrl";
import Text from "./Text";
import InfoButton from "./InfoButton";
import merge from "lodash/merge";
import { getTextLineCount } from "../utils/measure-text";
import { TSFontSpecs } from "react-native-text-size";

export type MarkdownTextProps = {
    markdown: string;
    onLinkPress?: (url: string) => boolean;
    markdownStyle?: StyleSheet.NamedStyles<unknown>;
    allowFontScaling?: boolean;
    fontColor?: ColorValue;
    initialNumberOfLines?: number;
} & ViewProps;

type MdStaleProps = {
    fontColor?: ColorValue;
};

export const useNormalMarkdownStyle = ({ fontColor: color }: MdStaleProps) => {
    const { fonts, gridSize, colors } = useTheme();
    return useMemo(() => {
        return {
            // The main container

            // @pseudo class, does not have a unique render rule
            heading1: {
                marginBottom: gridSize / 4,
                ...fonts.titleLarge,
                ...(color ? { color } : {}),
                flexDirection: "row",
            },
            heading2: {
                marginBottom: gridSize / 4,
                ...fonts.titleMedium,
                ...(color ? { color } : {}),
                flexDirection: "row",
            },
            heading3: {
                marginBottom: gridSize / 4,
                ...fonts.titleSmall,
                ...(color ? { color } : {}),
                flexDirection: "row",
            },
            heading4: {
                marginBottom: gridSize / 4,
                ...fonts.bodyLarge,
                ...(color ? { color } : {}),
                flexDirection: "row",
            },
            heading5: {
                marginBottom: gridSize / 4,
                ...fonts.bodyMedium,
                ...(color ? { color } : {}),
                flexDirection: "row",
            },
            heading6: {
                marginBottom: gridSize / 4,
                ...fonts.bodySmall,
                ...(color ? { color } : {}),
                flexDirection: "row",
            },
            bullet_list: {
                ...fonts.bodyMedium,
                ...(color ? { color } : {}),
                lineHeight: 24,
            },
            bullet_list_content: {
                ...fonts.bodyMedium,
                ...(color ? { color } : {}),
                lineHeight: 24,
            },
            ordered_list: {
                ...fonts.bodyMedium,
                ...(color ? { color } : {}),
                lineHeight: 24,
            },
            paragraph: {
                marginTop: gridSize / 4,
                marginBottom: 0,
                ...fonts.bodyMedium,
                ...(color ? { color } : {}),
            },
            bold: {
                ...fonts.bodyLarge,
                ...(color ? { color } : {}),
            },
            strong: {
                ...fonts.bodyLarge,
                ...(color ? { color } : {}),
            },
            bullet_list_icon: {
                marginLeft: 5,
                marginRight: 10,
            },
            hardbreak: {
                width: "100%",
                height: 1,
            },
            link: {
                color: colors.secondary,
            },
            blocklink: {
                color: colors.secondary,
            },
            helpButton: {
                margin: 0,
                marginHorizontal: 4,
                borderColor: colors.onWarningBackground,
                borderWidth: 1,
                borderRadius: 8,
                backgroundColor: colors.warningBackground,
            },
        };
    }, [color, fonts, colors, gridSize]);
};

const handleHtml: RenderFunction = (
    node: ASTNode,
    children: ReactNode[],
    parent: ASTNode[],
    styles,
) => {
    if (node.content.trim() === "<br>") {
        return (
            <Text style={styles.hardbreak} key={node.key}>
                {""}
            </Text>
        );
    }

    return null;
};

type UseRulesParams = {
    allowFontScaling?: boolean;
    initialNumberOfLines?: number;
    setBestFitText: (v?: string) => void;
    hasBestFit: boolean;
};

const useRules = ({
    allowFontScaling,
    initialNumberOfLines,
    setBestFitText,
    hasBestFit,
}: UseRulesParams) => {
    const [isExtended, setIsExtended] = useState(initialNumberOfLines ? false : true);

    const onExtend = useCallback(() => {
        setBestFitText(undefined);
        setIsExtended(true);
    }, [setBestFitText]);

    // Memoize the render rules so the underlying <Markdown> gets a stable `rules` prop and does
    // not re-parse/re-render on every parent render (rows are recycled constantly while scrolling).
    return useMemo(
        () =>
            ({
                list_item: (node, children, parent, styles, inheritedStyles = {}) => {
                    // we need to grab any text specific stuff here that is applied on the list_item style
                    // and apply it onto bullet_list_icon. the AST renderer has some workaround code to make
                    // the content classes apply correctly to the child AST tree items as well
                    // as code that forces the creation of the inheritedStyles object for list_items
                    const refStyle: Record<string, string> = {
                        ...inheritedStyles,
                        ...StyleSheet.flatten(styles.list_item),
                    };

                    const arr: string[] = Object.keys(refStyle);

                    const modifiedInheritedStylesObj: Record<string, string> = {};

                    for (let b = 0; b < arr.length; b++) {
                        if (textStyleProps.includes(arr[b])) {
                            modifiedInheritedStylesObj[arr[b]] = refStyle[arr[b]];
                        }
                    }

                    if (hasParents(parent, "bullet_list")) {
                        return (
                            <View key={node.key} style={styles._VIEW_SAFE_list_item}>
                                <Text
                                    style={[modifiedInheritedStylesObj, styles.bullet_list_icon]}
                                    accessible={false}
                                    allowFontScaling={allowFontScaling}
                                >
                                    {Platform.select({
                                        android: "\u2022",
                                        ios: "\u2022", //"\u25cf",
                                        default: "\u2022",
                                    })}
                                </Text>
                                <View
                                    style={[
                                        styles._VIEW_SAFE_bullet_list_content,
                                        styles.bullet_list,
                                    ]}
                                >
                                    {children}
                                </View>
                            </View>
                        );
                    }

                    if (hasParents(parent, "ordered_list")) {
                        const orderedListIndex = parent.findIndex(
                            (el) => el.type === "ordered_list",
                        );

                        const orderedList = parent[orderedListIndex];
                        let listItemNumber;

                        if (orderedList.attributes && orderedList.attributes.start) {
                            listItemNumber = orderedList.attributes.start + node.index;
                        } else {
                            listItemNumber = node.index + 1;
                        }

                        return (
                            <View key={node.key} style={styles._VIEW_SAFE_list_item}>
                                <Text
                                    style={[modifiedInheritedStylesObj, styles.ordered_list_icon]}
                                    allowFontScaling={allowFontScaling}
                                >
                                    {listItemNumber}
                                    {node.markup}
                                </Text>
                                <View style={styles._VIEW_SAFE_ordered_list_content}>
                                    {children}
                                </View>
                            </View>
                        );
                    }

                    // we should not need this, but just in case
                    return (
                        <View key={node.key} style={styles._VIEW_SAFE_list_item}>
                            {children}
                        </View>
                    );
                },
                link: (node, children, parent, styles, onLinkPress: (url: string) => boolean) => {
                    const href: string = node.attributes.href;
                    if (href.startsWith("help:")) {
                        return (
                            <InfoButton key={node.key} onPress={() => openUrl(href, onLinkPress)} />
                        );
                    }
                    return (
                        <Text
                            key={node.key}
                            style={styles.link}
                            onPress={() => openUrl(href, onLinkPress)}
                            allowFontScaling={allowFontScaling}
                        >
                            {children}
                        </Text>
                    );
                },
                text: (node, _1, _2, _3, style) => {
                    return (
                        <Text key={node.key} style={style} allowFontScaling={allowFontScaling}>
                            {node.content}
                        </Text>
                    );
                },
                textgroup: (node, children, parent, styles) => (
                    <Text
                        key={node.key}
                        style={styles.textgroup}
                        allowFontScaling={allowFontScaling}
                    >
                        {children}
                    </Text>
                ),
                paragraph: (node, children, parent, styles) => {
                    const TextComponent = !isExtended ? ExtendableText : Text;

                    return (
                        <View
                            key={node.key}
                            style={[
                                styles._VIEW_SAFE_paragraph,
                                !isExtended && { flexDirection: "row" },
                            ]}
                        >
                            <TextComponent
                                allowFontScaling={allowFontScaling}
                                style={[styles._VIEW_SAFE_paragraph, styles.paragraph]}
                                onExtend={onExtend}
                                numberOfLines={!isExtended ? initialNumberOfLines : undefined}
                                setBestFitText={setBestFitText}
                                hasBestFit={hasBestFit}
                            >
                                {children}
                            </TextComponent>
                        </View>
                    );
                },
                html_block: handleHtml,
                html_inline: handleHtml,
                // For some reason, Strikethrough is doubled on web. This function prevents that.
                s: (node, children) => children,
                image: (node, _children, _parent, styles) => {
                    const { src } = node.attributes;
                    const content = node.content || "";
                    const [name, size] = content.split("=");
                    const [w, h] = size ? size.split("x").map(Number) : "";
                    const height = h || 100;
                    const width = w || 100;
                    return (
                        <Image
                            key={node.key}
                            source={{ uri: src }}
                            style={[styles.picture, { width, height }]} // Default size, can be overridden
                            resizeMode="contain"
                            accessibilityLabel={name || "Image"}
                        />
                    );
                },
            }) as { [key: string]: RenderFunction },
        [allowFontScaling, initialNumberOfLines, isExtended, onExtend, setBestFitText, hasBestFit],
    );
};

type ExtendableTextProps = PropsWithChildren<{
    numberOfLines?: number;
    allowFontScaling?: boolean;
    style?: StyleProp<TextStyle>;
    onExtend?: () => void;
    setBestFitText: (v?: string) => void;
    hasBestFit: boolean;
}>;

const ExtendableText = ({
    allowFontScaling,
    style,
    children,
    onExtend,
    numberOfLines,
    setBestFitText,
    hasBestFit,
}: ExtendableTextProps) => {
    const { colors } = useTheme();
    const [viewWidth, setViewWidth] = useState<number>();
    const [textLines, setTextLines] = useState<TextLayoutLine[]>();

    useEffect(() => {
        if (viewWidth && textLines && numberOfLines) {
            void (async () => {
                const fullString = textLines.reduce(
                    (result, value) => `${result}${value.text}`,
                    ``,
                );
                let start = 0;
                let end = fullString.length;
                let bestFit = fullString;

                const flattenStyle = StyleSheet.flatten(style);
                const lineCountStyle = {
                    fontFamily: flattenStyle.fontFamily,
                    fontSize: flattenStyle.fontSize,
                    letterSpacing: flattenStyle.letterSpacing,
                };
                const isLongerThanMaxDescriptionLines =
                    (await getTextLineCount(fullString, viewWidth, lineCountStyle)) > numberOfLines;

                // If the text is shorter than `numberOfLines` AND was not already truncated (hasBestFit = true), do not "extend" it automatically
                if (!isLongerThanMaxDescriptionLines && !hasBestFit) {
                    onExtend?.();
                } else if (Platform.OS === "android") {
                    // On Android, the native implementation of `ellipsizeMode: "clip"` remove the whole last word of the line.
                    // So we must calculate the text length manually to truncate the original text at the correct position.
                    while (isLongerThanMaxDescriptionLines && start <= end) {
                        const mid = Math.floor((start + end) / 2);
                        const testText = `${fullString.slice(0, mid)}`;

                        const lineCount = await getTextLineCount(
                            testText,
                            viewWidth,
                            lineCountStyle,
                        );

                        if (lineCount > numberOfLines) {
                            end = mid - 1; // Réduire la longueur du texte
                        } else {
                            bestFit = testText;
                            start = mid + 1; // Essayer un texte plus long
                        }
                    }

                    setBestFitText(bestFit);
                }
            })();
        }
    }, [hasBestFit, numberOfLines, onExtend, setBestFitText, style, textLines, viewWidth]);

    return (
        <>
            <Text
                allowFontScaling={allowFontScaling}
                style={{
                    overflow: "hidden",
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore required for web to avoid the default "textOverflow: ellipsis"
                    textOverflow: "clip",
                    flexShrink: 1,
                    marginLeft: 10,
                }}
                {...(numberOfLines
                    ? {
                          numberOfLines,
                          ellipsizeMode: "clip",
                      }
                    : {})}
                onLayout={(e) => {
                    if (!viewWidth) {
                        setViewWidth(e.nativeEvent.layout.width);
                    }
                    if (Platform.OS === "web") {
                        const typedChildren = children as [
                            // Format sent by @code-atlas/react-native-markdown-display
                            {
                                props: {
                                    children: [
                                        {
                                            props: {
                                                children:
                                                    | string
                                                    | [{ props: { children: string } }];
                                                style: TSFontSpecs;
                                            };
                                        },
                                    ];
                                };
                            },
                        ];
                        const allChildrenText = typedChildren[0].props.children.reduce(
                            (result, value) => {
                                if (typeof value.props.children === "string") {
                                    return `${result}${value.props.children}`;
                                } else {
                                    return `${result}${value.props.children[0].props.children}`;
                                }
                            },
                            "",
                        );
                        const { style: textStyle } = typedChildren[0].props.children[0].props;

                        void (async () => {
                            const lineCount = await getTextLineCount(
                                allChildrenText,
                                e.nativeEvent.layout.width + 1, // + 1 because the layout.width value is rounded.
                                textStyle,
                            );

                            if (lineCount === numberOfLines) {
                                onExtend?.();
                            }
                        })();
                    }
                }}
                // React-Native only
                onTextLayout={(e) => {
                    if (!textLines) {
                        setTextLines(e.nativeEvent.lines);
                    }
                }}
            >
                {children}
            </Text>
            {numberOfLines && (
                <Text
                    onPress={onExtend}
                    allowFontScaling={allowFontScaling}
                    numberOfLines={1}
                    style={[
                        style,
                        {
                            marginTop: 0,
                            marginLeft: 0,
                            color: colors.secondary,
                            paddingLeft: 2,
                            // Keep the button at its full width: the truncated label
                            // (flexShrink: 1) shrinks instead, so "[...]" never wraps to a 2nd line.
                            flexShrink: 0,
                        },
                    ]}
                >
                    [...]
                </Text>
            )}
        </>
    );
};

const MarkdownView = ({
    markdown,
    onLinkPress,
    markdownStyle,
    fontColor,
    allowFontScaling,
    initialNumberOfLines,
    ref,
    ...props
}: MarkdownTextProps & { ref?: React.Ref<View> }) => {
    const [bestTextFit, setBestFitText] = useState<string>();
    const mdStyle = useNormalMarkdownStyle({ fontColor });
    const style = useMemo(() => merge(mdStyle, markdownStyle), [markdownStyle, mdStyle]);
    const rules = useRules({
        allowFontScaling,
        initialNumberOfLines,
        setBestFitText,
        hasBestFit: !!bestTextFit,
    });

    // The parser configuration is static, so build the MarkdownIt instance once instead of on
    // every render (it is re-parsed on each row recycle in long virtualized lists).
    const markdownItInstance = useMemo(() => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
        return MarkdownIt({
            html: true,
            typographer: true,
            linkify: false,
            breaks: true,
        });
    }, []);

    return (
        <View ref={ref} {...props}>
            {/* Markdown forgot to add children properties*/}
            {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
            {/* @ts-ignore */}
            <Markdown
                markdownit={markdownItInstance}
                style={style}
                onLinkPress={onLinkPress}
                rules={rules}
                mergeStyle={false}
            >
                {bestTextFit || markdown}
            </Markdown>
        </View>
    );
};

export default MarkdownView;
